/**
 * Lo stato di visione e i consigliati.
 *
 * Portate dal prototipo v3 (`vStat`, `recs`, `contList`, `latest`, `contCard`), come dice
 * 01-ARCHITETTURA §3: «sono già state discusse guardando uno schermo». Qui sono tipizzate
 * e rinominate in inglese, ma il **comportamento è identico** — i pesi, le soglie,
 * l'ordinamento e il taglio a dieci sono quelli.
 *
 * Non è codice della fase 1: è la metà portabile, e sta qui perché la fase 4 la riusi
 * senza riscriverla e perché il telefono (fase 5) non la ridiscuta.
 */

export type VodKind = 'movie' | 'series';

export interface VodItem {
  readonly id: string;
  readonly kind: VodKind;
  readonly title: string;
  /** Genere come arriva dal catalogo: **non tradotto** (14-LINGUE-E-TESTI §1). */
  readonly genre: string;
  /**
   * Famiglia di generi. Il prototipo la chiama `fam`: è ciò su cui pesano i consigliati.
   *
   * Su un catalogo vero la riempie la **categoria**, non il genere scritto sulla scheda: le categorie
   * di un fornitore VOD *sono* i generi («Azione», «Commedia»), sono un insieme chiuso e le porta ogni
   * titolo. Il campo `genre` invece è testo libero, spesso vuoto e spesso multiplo («Azione,
   * Thriller»): pesarci i consigliati vorrebbe dire una famiglia per combinazione di parole.
   */
  readonly genreFamily: string;
  /** L'anno, o `0` quando il catalogo non lo dichiara: non si indovina dal titolo. */
  readonly year: number;
  /** La locandina, o `null`. Verticale 2:3 nel disegno. */
  readonly posterUrl: string | null;
  /** La categoria a cui il catalogo lo assegna. È anche `genreFamily`, e resta separata perché una
   *  è un identificativo con cui si chiede al gateway e l'altra è una chiave di raggruppamento. */
  readonly categoryId: string;
  /**
   * Numero di episodi per stagione. Vuoto per i film — **e vuoto anche per una serie appena
   * arrivata dal listino**: le stagioni le dice `get_series_info`, cioè una chiamata per serie, e
   * farne una per ogni piastrella di una fascia sarebbe cinquanta chiamate per disegnare uno schermo.
   * Si riempie quando si apre la scheda.
   */
  readonly episodesPerSeason: ReadonlyArray<number>;
  /**
   * Quando è entrato in catalogo, o `null` se il catalogo non lo dice.
   *
   * Un **istante**, non «da quanti giorni»: i giorni dipendono da quando si guarda lo schermo, e un
   * numero di giorni archiviato in locale invecchia da solo — la stessa trappola delle finestre di
   * prova che scadono col calendario. La frase «aggiunto tre giorni fa» la compone chi disegna, che
   * ha l'istante di adesso.
   */
  readonly addedAtMs: number | null;
  /** Già risolto dal backend con l'eredità dal bouquet (05-CONTRATTO-API §9.1). */
  readonly isAdult: boolean;
}

/** Percentuali di visione. Film: `id → 0..100`. Serie: `id → "stagione-episodio" → 0..100`. */
export interface WatchProgress {
  readonly movies: Readonly<Record<string, number>>;
  readonly episodes: Readonly<Record<string, Readonly<Record<string, number>>>>;
}

export interface EpisodeRef {
  readonly season: number;
  readonly episode: number;
  readonly percent: number;
}

export interface WatchState {
  readonly watched: boolean;
  readonly started: boolean;
  /** Va nella fascia «Continua a guardare». Prototipo: `cont`. */
  readonly continueWatching: boolean;
  /** Percentuale da mostrare sulla barra; 0 quando non va mostrata. */
  readonly percent: number;
  /** Episodi finiti. Zero per i film. */
  readonly doneEpisodes: number;
  readonly totalEpisodes: number;
  /** Il prossimo da guardare, o `null` quando non ce n'è. Prototipo: `next`. */
  readonly next: EpisodeRef | null;
}

export const WATCHED_PERCENT = 100;

/**
 * Sotto questa percentuale **non si registra niente**.
 *
 * Aprire un titolo, capire che non era quello e uscire lascia due secondi di visione: registrarli
 * metterebbe il titolo in «Continua a guardare», da dove non se ne va più — e quella fascia si
 * riempirebbe di cose che nessuno ha guardato, cioè diventerebbe inutile proprio per chi la usa.
 */
export const PROGRESS_MIN_PERCENT = 2;

/**
 * Sopra questa percentuale si segna **visto**, e si arrotonda a 100.
 *
 * I titoli di coda non si guardano: un film lasciato al 96% è un film finito, e pretendere il 100
 * significa che «Continua a guardare» conserva per sempre i quattro minuti di crediti di ogni cosa
 * che si è vista fino in fondo.
 */
export const PROGRESS_DONE_PERCENT = 95;

/** La chiave di un episodio dentro `WatchProgress.episodes`. Una funzione, perché la stessa forma
 *  la scrivono chi salva e chi legge: due grafie diverse sono un avanzamento che non si ritrova. */
export function episodeProgressKey(season: number, episode: number): string {
  return `${season}-${episode}`;
}

/** Quanto si è visto, 0–100, oppure `null` quando la durata non si conosce ancora. */
export function watchedPercent(positionSec: number, durationSec: number): number | null {
  if (!Number.isFinite(positionSec) || !Number.isFinite(durationSec) || durationSec <= 0) return null;
  if (positionSec < 0) return null;
  return Math.min(100, Math.round((positionSec / durationSec) * 100));
}

/** Il valore da archiviare per una percentuale osservata, o `null` se non va archiviata affatto. */
function storablePercent(percent: number): number | null {
  if (!Number.isFinite(percent) || percent < PROGRESS_MIN_PERCENT) return null;
  return percent >= PROGRESS_DONE_PERCENT ? WATCHED_PERCENT : Math.round(percent);
}

export function recordMovieProgress(
  progress: WatchProgress,
  movieId: string,
  percent: number,
): WatchProgress {
  const value = storablePercent(percent);
  if (value === null) return progress;
  return { ...progress, movies: { ...progress.movies, [movieId]: value } };
}

export function recordEpisodeProgress(
  progress: WatchProgress,
  seriesId: string,
  season: number,
  episode: number,
  percent: number,
): WatchProgress {
  const value = storablePercent(percent);
  if (value === null) return progress;
  const before = progress.episodes[seriesId] ?? {};
  return {
    ...progress,
    episodes: {
      ...progress.episodes,
      [seriesId]: { ...before, [episodeProgressKey(season, episode)]: value },
    },
  };
}

export const EMPTY_PROGRESS: WatchProgress = { movies: {}, episodes: {} };

/**
 * `vStat` del prototipo v3, pari pari.
 *
 * Film: visto a 100, iniziato sopra zero, e la barra si mostra solo fra i due.
 * Serie: si appiattiscono le stagioni in un elenco di episodi; l'ultimo toccato decide
 * dove si riprende — se non è finito si riprende lui, altrimenti il successivo.
 */
export function watchStateOf(item: VodItem, progress: WatchProgress): WatchState {
  if (item.kind === 'movie') {
    const percent = progress.movies[item.id] ?? 0;
    return {
      watched: percent >= WATCHED_PERCENT,
      started: percent > 0,
      continueWatching: percent > 0 && percent < WATCHED_PERCENT,
      percent: percent > 0 && percent < WATCHED_PERCENT ? percent : 0,
      doneEpisodes: 0,
      totalEpisodes: 0,
      next: null,
    };
  }

  const seen = progress.episodes[item.id] ?? {};

  /*
    **Il listino non porta le stagioni, e qui stava il difetto che svuotava «Continua a guardare».**

    `parseSeriesList` lascia `episodesPerSeason` vuoto — le stagioni stanno in `get_series_info`,
    cioè una chiamata per serie — e con l'elenco vuoto il ciclo qui sotto non produce nessun
    episodio: `lastTouched` resta a `-1`, `continueWatching` esce falso, e **una serie guardata a
    metà non compariva mai** né nella fascia della home né nella vetrina on demand. Solo la scheda la
    mostrava giusta, perché è l'unico posto dove le stagioni erano già state chieste.

    Il sintomo che ne usciva è «l'avanzamento delle serie non si salva»: si salvava, e non lo
    leggeva nessuno.

    La cura sta in due punti, e questo è il secondo. Il primo è l'indice delle stagioni
    sull'apparecchio (`CACHE_KEYS.seriesSeasons`), che restituisce il conteggio vero a ogni serie mai
    aperta — cioè a tutte quelle su cui si può avere un avanzamento. Questo ramo è quel che resta:
    un avanzamento arrivato da una versione precedente, o un archivio ripulito a metà. Lì il totale
    **non si conosce**, e il codice non lo inventa: si ricostruisce l'elenco dagli episodi
    registrati, si dice «iniziata» e non si dice mai «vista» — perché non si può sapere, e dichiarare
    finita una serie che non lo è la toglie dalla fascia in cui chi guarda la sta cercando.
  */
  if (item.episodesPerSeason.length === 0) return recordedSeriesState(seen);

  const episodes: EpisodeRef[] = [];
  item.episodesPerSeason.forEach((count, seasonIndex) => {
    for (let episode = 1; episode <= count; episode += 1) {
      const season = seasonIndex + 1;
      episodes.push({ season, episode, percent: seen[`${season}-${episode}`] ?? 0 });
    }
  });

  let lastTouched = -1;
  episodes.forEach((entry, index) => {
    if (entry.percent > 0) lastTouched = index;
  });

  const doneEpisodes = episodes.filter((entry) => entry.percent >= WATCHED_PERCENT).length;
  const next =
    lastTouched < 0
      ? episodes[0] ?? null
      : (episodes[lastTouched] as EpisodeRef).percent < WATCHED_PERCENT
        ? (episodes[lastTouched] as EpisodeRef)
        : episodes[lastTouched + 1] ?? null;
  const watched = episodes.length > 0 && doneEpisodes === episodes.length;

  return {
    watched,
    started: lastTouched >= 0,
    continueWatching: lastTouched >= 0 && !watched,
    percent:
      lastTouched >= 0 && next && next.percent > 0 && next.percent < WATCHED_PERCENT ? next.percent : 0,
    doneEpisodes,
    totalEpisodes: episodes.length,
    next,
  };
}

/**
 * Lo stato di una serie di cui **non si conosce il numero di episodi**, ricostruito da quelli
 * registrati.
 *
 * Serve quando `episodesPerSeason` è vuoto: vedi la nota dentro `watchStateOf`. Qui si sa cosa è
 * stato guardato e non si sa quanto manca, e le due cose vanno tenute distinte invece di far finta
 * che la seconda sia zero.
 *
 * - `watched` è **sempre falso**. Senza il totale non si può dimostrare che sia finita, e dirlo
 *   toglierebbe la serie dalla fascia in cui chi guarda la sta cercando. Il caso opposto — una serie
 *   davvero finita che resta in «Continua a guardare» — dura finché non si riapre la scheda, che
 *   ripopola l'indice delle stagioni con il conteggio vero.
 * - `totalEpisodes` resta `0`, che è come si dice «non lo so» a chi disegna: la scheda mostra
 *   `3 / 10` solo quando il totale c'è.
 * - `next` è l'ultimo episodio toccato se non è finito, altrimenti `null` — il successivo non si può
 *   nominare senza sapere quanti sono.
 */
function recordedSeriesState(seen: Readonly<Record<string, number>>): WatchState {
  const episodes: EpisodeRef[] = [];
  for (const key of Object.keys(seen)) {
    const parsed = parseEpisodeProgressKey(key);
    if (parsed === null) continue;
    episodes.push({ season: parsed.season, episode: parsed.episode, percent: seen[key] ?? 0 });
  }
  // Nell'ordine in cui si guardano: le chiavi di un oggetto arrivano nell'ordine in cui sono state
  // scritte, che è l'ordine in cui si è premuto play — non quello della serie.
  episodes.sort((a, b) => a.season - b.season || a.episode - b.episode);

  const touched = episodes.filter((entry) => entry.percent > 0);
  if (touched.length === 0) {
    return {
      watched: false,
      started: false,
      continueWatching: false,
      percent: 0,
      doneEpisodes: 0,
      totalEpisodes: 0,
      next: null,
    };
  }

  const last = touched[touched.length - 1] as EpisodeRef;
  const next = last.percent < WATCHED_PERCENT ? last : null;
  return {
    watched: false,
    started: true,
    continueWatching: true,
    percent: next !== null ? next.percent : 0,
    doneEpisodes: touched.filter((entry) => entry.percent >= WATCHED_PERCENT).length,
    totalEpisodes: 0,
    next,
  };
}

/**
 * L'inverso di `episodeProgressKey`. `null` per una chiave che non ha quella forma.
 *
 * Scartare invece di interpretare: un archivio di un'app più vecchia, o scritto a mano, non deve
 * produrre una stagione `NaN` che poi si ordina a caso.
 */
export function parseEpisodeProgressKey(key: string): { season: number; episode: number } | null {
  const separator = key.indexOf('-');
  if (separator <= 0) return null;
  const season = Number(key.slice(0, separator));
  const episode = Number(key.slice(separator + 1));
  if (!Number.isInteger(season) || !Number.isInteger(episode)) return null;
  if (season <= 0 || episode <= 0) return null;
  return { season, episode };
}

/**
 * Il titolo del listino con il conteggio degli episodi che l'apparecchio ha già visto passare.
 *
 * Gemella di `withSeasons`, che fa la stessa cosa partendo dalla scheda appena arrivata. Questa
 * parte dall'indice locale (`CACHE_KEYS.seriesSeasons`), e serve dove la scheda non c'è: la home e
 * la vetrina, che hanno solo il listino e devono comunque sapere se una serie è a metà.
 */
export function withEpisodeCounts(item: VodItem, counts: ReadonlyArray<number>): VodItem {
  if (counts.length === 0) return item;
  return { ...item, episodesPerSeason: counts };
}

export interface Recommendation {
  readonly item: VodItem;
  readonly score: number;
  /** L'id del titolo che ha generato il consiglio. La frase la compone l'i18n. */
  readonly becauseOfId: string;
  readonly becauseOfTitle: string;
}

export const RECOMMENDATION_LIMIT = 10;

/**
 * `recs` del prototipo v3, pari pari.
 *
 * Pesa le famiglie di genere di ciò che è stato visto — un film visto vale 2, iniziato 1;
 * una serie vista vale 3, iniziata 2 se ha almeno tre episodi finiti e 1 altrimenti —
 * esclude i titoli già cominciati, e ordina per peso più un pizzico di novità
 * (`(anno - 2020) * 0,1`), che serve a rompere le parità senza ribaltare l'ordine.
 *
 * **Il motivo va mostrato sempre**: una fascia «Consigliati per te» senza il perché è
 * indistinguibile da una riempita a caso, e dopo tre settimane l'utente smette di
 * guardarla (05-CONTRATTO-API §4.4).
 */
export function recommendations(
  catalog: ReadonlyArray<VodItem>,
  progress: WatchProgress,
  kind?: VodKind,
): ReadonlyArray<Recommendation> {
  const weightByFamily = new Map<string, number>();
  const sourceByFamily = new Map<string, { weight: number; id: string; title: string }>();

  for (const item of catalog) {
    const state = watchStateOf(item, progress);
    const weight =
      item.kind === 'movie'
        ? state.watched
          ? 2
          : state.started
            ? 1
            : 0
        : state.watched
          ? 3
          : state.started
            ? state.doneEpisodes >= 3
              ? 2
              : 1
            : 0;
    if (weight === 0) continue;
    weightByFamily.set(item.genreFamily, (weightByFamily.get(item.genreFamily) ?? 0) + weight);
    const current = sourceByFamily.get(item.genreFamily);
    if (!current || weight > current.weight) {
      sourceByFamily.set(item.genreFamily, { weight, id: item.id, title: item.title });
    }
  }

  return catalog
    .filter((item) => (!kind || item.kind === kind) && weightByFamily.has(item.genreFamily))
    .filter((item) => !watchStateOf(item, progress).started)
    .map((item) => {
      const source = sourceByFamily.get(item.genreFamily) as { id: string; title: string };
      return {
        item,
        score: (weightByFamily.get(item.genreFamily) ?? 0) + (item.year - 2020) * 0.1,
        becauseOfId: source.id,
        becauseOfTitle: source.title,
      };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, RECOMMENDATION_LIMIT);
}

/**
 * `contList` del prototipo: i titoli da riprendere, nell'ordine in cui sono stati
 * guardati l'ultima volta. Chi non è nell'elenco dei recenti finisce in fondo.
 */
export function continueWatching(
  catalog: ReadonlyArray<VodItem>,
  progress: WatchProgress,
  recentIds: ReadonlyArray<string>,
  kind?: VodKind,
): ReadonlyArray<VodItem> {
  const rank = (id: string): number => {
    const index = recentIds.indexOf(id);
    return index < 0 ? Number.MAX_SAFE_INTEGER : index;
  };
  return catalog
    .filter((item) => (!kind || item.kind === kind) && watchStateOf(item, progress).continueWatching)
    .sort((a, b) => rank(a.id) - rank(b.id));
}

export const LATEST_LIMIT = 10;

/**
 * `latest` del prototipo: i più recenti in catalogo, dieci al massimo.
 *
 * Chi non dichiara un istante d'inserimento va **in fondo**, non in testa: su un catalogo importato
 * da un fornitore il campo manca a macchia, e trattare «non lo so» come «adessissimo» metterebbe in
 * cima ai nuovi arrivi esattamente i titoli di cui non si sa niente.
 */
export function latestArrivals(
  catalog: ReadonlyArray<VodItem>,
  kind?: VodKind,
): ReadonlyArray<VodItem> {
  return catalog
    .filter((item) => !kind || item.kind === kind)
    .slice()
    /*
      Il confronto passa da due `if` e non da una sottrazione.

      `(b.addedAtMs ?? -Infinity) - (a.addedAtMs ?? -Infinity)` dà `NaN` quando **entrambi** mancano,
      e un comparatore che torna `NaN` lascia l'ordine a discrezione del motore: su V8 due titoli
      senza data si scambiano di posto fra due disegni, cioè una fascia che balla senza che nulla sia
      cambiato.
    */
    .sort((a, b) => {
      if (a.addedAtMs === b.addedAtMs) return 0;
      if (a.addedAtMs === null) return 1;
      if (b.addedAtMs === null) return -1;
      return b.addedAtMs - a.addedAtMs;
    })
    .slice(0, LATEST_LIMIT);
}
